import { AprDetails, BsolStatsData } from './types';
import { getMarketIconSrc } from 'src/components/Utils/MarketIcon';
import { getApyFromApr } from 'src/utils/borrowLend';
import { LBTC_API_RESPONSE } from '../interestBreakdown/useLbtcFetch';
import { SanctumApiResponse } from './types';
import { BnsolApiResponse } from './types';
import { SusdeApiResponse } from './types';
import { JlpPoolData } from './types';
import { SyrupUsdcApiResponse } from './types';
import { SpotMarketConfig } from '@drift-labs/sdk';
import { BSOL_MARKET_INDEX } from 'src/environmentVariables/EnvironmentVariables';

/**
 * Process Sanctum LST data into native APY breakdowns
 */
export function processSanctumLstData(
	sanctumData: SanctumApiResponse,
	spotMarkets: SpotMarketConfig[],
	JITO_SOL_MARKET_INDEX: number,
	EXPONENT_POOL_JITOSOL_MARKET_INDEX: number,
	LST_POOL_JITOSOL_MARKET_INDEX: number
): Record<number, AprDetails[]> {
	const result: Record<number, AprDetails[]> = {};

	Object.keys(sanctumData.apys).forEach((key) => {
		const marketIndex = spotMarkets.find(
			(mkt) => mkt.symbol === key
		)?.marketIndex;
		if (marketIndex !== undefined) {
			result[marketIndex] = [
				{
					type: 'native',
					source: `${key} APY`,
					apy: sanctumData.apys[key] * 100,
				},
			];
		}
	});

	// Add jitoSOL data to isolated pools
	const jitoSolData = result[JITO_SOL_MARKET_INDEX];
	if (jitoSolData) {
		result[EXPONENT_POOL_JITOSOL_MARKET_INDEX] = [...jitoSolData];
		result[LST_POOL_JITOSOL_MARKET_INDEX] = [...jitoSolData];
	}

	return result;
}

/**
 * Process BNSOL data into native APY breakdown
 */
export function processBnsolData(
	bnsolData: BnsolApiResponse,
	BNSOL_MARKET_INDEX: number
): Record<number, AprDetails[]> {
	const apy = +bnsolData.data.apy;
	return {
		[BNSOL_MARKET_INDEX]: [
			{
				type: 'native',
				source: 'BNSOL APY',
				apy: isNaN(apy) ? 0 : apy * 100,
			},
		],
	};
}

/**
 * Process Exponent data into native APY breakdowns
 */
export function processExponentData(
	exponentData: { kySolApy: number; fragSolApy: number },
	KY_SOL_MARKET_INDEX: number,
	FRAG_SOL_MARKET_INDEX: number
): Record<number, AprDetails[]> {
	return {
		[KY_SOL_MARKET_INDEX]: [
			{
				type: 'native',
				source: 'kySOL APY',
				apy: exponentData.kySolApy,
			},
		],
		[FRAG_SOL_MARKET_INDEX]: [
			{
				type: 'native',
				source: 'fragSOL APY',
				apy: exponentData.fragSolApy,
			},
		],
	};
}

/**
 * Process bSOL data into native APY and reward breakdowns
 */
export function processBsolData(
	bsolData: BsolStatsData
): Record<number, AprDetails[]> {
	const baseApy = bsolData?.stats?.apy.base;
	const lendingMultiplier = bsolData?.stats?.apy.lending;
	const blzeApy = (bsolData?.stats?.apy.blze ?? 0) * (lendingMultiplier ?? 1);

	return {
		[BSOL_MARKET_INDEX]: [
			{
				type: 'native',
				source: 'bSOL APY',
				apy: baseApy ?? 0,
			},
			{
				type: 'lend-reward',
				source: 'BLZE Rewards',
				apy: blzeApy,
				icon: '/assets/icons/blze.svg',
			},
		],
	};
}

/**
 * Process sUSDe data into native APY breakdown
 */
export function processSusdeData(
	susdeData: SusdeApiResponse,
	SUSDE_MARKET_INDEX: number
): Record<number, AprDetails[]> {
	const susdeYield = susdeData?.stakingYield?.value ?? 0;
	return {
		[SUSDE_MARKET_INDEX]: [
			{
				type: 'native',
				source: 'sUSDe Yield',
				apy: susdeYield,
				icon: getMarketIconSrc({ baseSymbol: 'susde' }),
				helperText:
					'Earn 5 Sats a day per sUSDe. Learn More at https://www.drift.trade/updates/drift-partners-with-ethena-to-launch-usde-and-susde-on-solana',
			},
		],
	};
}

/**
 * Process JLP data into native APY breakdown
 */
export function processJlpData(
	jlpData: JlpPoolData,
	JLP_POOL_JLP_MARKET_INDEX: number,
	JLP_MARKET_INDEX: number
): Record<number, AprDetails[]> {
	const newApr = jlpData.feeAprBps / 100;
	const newApy = getApyFromApr(newApr, 'hourly');

	const jlpMetric = {
		type: 'native' as const,
		source: 'JLP Fees Yield',
		apy: newApy,
		apr: newApr,
		icon: getMarketIconSrc({ baseSymbol: 'jlp' }),
	};

	return {
		[JLP_POOL_JLP_MARKET_INDEX]: [jlpMetric],
		[JLP_MARKET_INDEX]: [jlpMetric],
	};
}

/**
 * Process syrupUSDC data into native APY breakdown
 */
export function processSyrupUsdcData(
	syrupData: SyrupUsdcApiResponse,
	SYRUP_USDC_MARKET_INDEX: number
): Record<number, AprDetails[]> {
	return {
		[SYRUP_USDC_MARKET_INDEX]: [
			{
				type: 'native',
				source: 'syrupUSDC Yield',
				apy:
					syrupData.poolV2.weeklyApy / 10_000_000_000_000_000_000_000_000_000,
			},
		],
	};
}

export function processLbtcData(
	lbtcData: LBTC_API_RESPONSE,
	LBTC_MARKET_INDEX: number
): Record<number, AprDetails[]> {
	return {
		[LBTC_MARKET_INDEX]: [
			{
				type: 'native',
				source: 'LBTC Yield',
				apy: lbtcData.lbtc_estimated_apy * 100,
			},
		],
	};
}
